import cx from "classnames"
import { AlertCircle, CheckCircle2, FilePlus, FileText, FolderOpen, GitBranch, Pencil, Search, Terminal } from "lucide-react"
import { useCallback, useMemo } from "react"
import type { Comment, CommentSource } from "../../../types"
import { type AnnotationSide, type CommentHandlers, FileDiffViewer, FileViewer, parsePatchFiles } from "../../FilesAndDiffs"
import type { BashGroup, CommentContext, GroupRenderer } from "../../events/messageGroups"
import { type BashSemanticType, classifyBashCommand } from "./classifyBashCommand"
import { type CommandOutputPresentation, getCommandOutputPresentation } from "./commandOutput"

function TerminalContent({ group, ctx }: { group: BashGroup; ctx: CommentContext }) {
    const bashContent = useMemo(() => {
        const lines: string[] = [`$ ${group.command}`]
        if (group.result !== undefined) lines.push(group.result)
        return lines.join("\n")
    }, [group.command, group.result])

    const sourceMatch = useCallback(
        (c: Comment) => {
            const src = c.source
            return src.type === "bash_output" && src.actionEventId === ctx.actionEventId && src.toolUseId === group.toolUseId
        },
        [ctx.actionEventId, group.toolUseId]
    )

    const createSource = useCallback(
        (lineStart: number, lineEnd: number, _side: AnnotationSide): CommentSource => ({
            type: "bash_output",
            actionEventId: ctx.actionEventId,
            toolUseId: group.toolUseId,
            lineStart,
            lineEnd,
        }),
        [ctx.actionEventId, group.toolUseId]
    )

    const commentHandlers: CommentHandlers = useMemo(() => ({ taskId: ctx.taskId, sourceMatch, createSource }), [ctx.taskId, sourceMatch, createSource])

    return <FileViewer file={{ name: "terminal", contents: bashContent, lang: "bash" }} disableFileHeader commentHandlers={commentHandlers} />
}

function GitDiffContent({
    presentation,
    group,
    ctx,
}: {
    presentation: Extract<CommandOutputPresentation, { kind: "git_diff" }>
    group: BashGroup
    ctx: CommentContext
}) {
    const files = useMemo(() => {
        try {
            return parsePatchFiles(presentation.patch).flatMap((patch) => patch.files)
        } catch {
            return []
        }
    }, [presentation.patch])

    if (files.length === 0) return <TerminalContent group={group} ctx={ctx} />
    return (
        <div className="flex flex-col gap-px bg-border">
            {files.map((file, index) => (
                <FileDiffViewer key={`${file.name}-${index}`} fileDiff={file} diffStyle="unified" commentHandlers={null} />
            ))}
        </div>
    )
}

function GitStatusContent({ presentation }: { presentation: Extract<CommandOutputPresentation, { kind: "git_status" }> }) {
    return (
        <div className="py-1">
            {presentation.branch && <div className="px-3 py-1.5 text-xs font-mono text-muted border-b border-border">{presentation.branch}</div>}
            {presentation.entries.length === 0 ? (
                <div className="px-3 py-2 text-xs text-success">Working tree clean</div>
            ) : (
                presentation.entries.map((entry) => (
                    <div key={`${entry.status}-${entry.path}`} className="flex min-w-0 items-center gap-2 px-3 py-1.5 text-xs">
                        <span
                            className={cx(
                                "w-6 flex-shrink-0 font-mono font-medium",
                                entry.status.includes("D") ? "text-error" : entry.status === "??" ? "text-success" : "text-warning"
                            )}
                        >
                            {entry.status}
                        </span>
                        <span className="truncate font-mono text-base-content">{entry.path}</span>
                    </div>
                ))
            )}
        </div>
    )
}

function GitLogContent({ presentation }: { presentation: Extract<CommandOutputPresentation, { kind: "git_log" }> }) {
    return (
        <div className="py-1">
            {presentation.commits.map((commit) => (
                <div key={commit.hash} className="flex min-w-0 items-baseline gap-2 px-3 py-1.5 text-xs">
                    <span className="flex-shrink-0 font-mono text-primary">{commit.hash.slice(0, 10)}</span>
                    <span className="truncate text-base-content">{commit.subject}</span>
                </div>
            ))}
        </div>
    )
}

function VerificationContent({
    presentation,
    group,
    ctx,
}: {
    presentation: Extract<CommandOutputPresentation, { kind: "verification" }>
    group: BashGroup
    ctx: CommentContext
}) {
    return (
        <div>
            <div className={cx("flex items-center gap-2 px-3 py-2 text-xs font-medium", group.isError ? "text-error" : "text-success")}>
                {group.isError ? <AlertCircle size="1em" /> : <CheckCircle2 size="1em" />}
                <span>{group.isError ? (presentation.failure ?? `${presentation.label} failed`) : presentation.summary}</span>
            </div>
            {presentation.output && (
                <div className="border-t border-border">
                    <TerminalContent group={group} ctx={ctx} />
                </div>
            )}
        </div>
    )
}

function BashContent({ group, ctx }: { group: BashGroup; ctx: CommentContext }) {
    const presentation = useMemo(() => getCommandOutputPresentation(group.command, group.result), [group.command, group.result])
    switch (presentation.kind) {
        case "git_diff":
            return <GitDiffContent presentation={presentation} group={group} ctx={ctx} />
        case "git_status":
            return <GitStatusContent presentation={presentation} />
        case "git_log":
            return <GitLogContent presentation={presentation} />
        case "verification":
            return <VerificationContent presentation={presentation} group={group} ctx={ctx} />
        case "terminal":
            return <TerminalContent group={group} ctx={ctx} />
    }
}

function getSemanticIcon(semanticType: BashSemanticType) {
    switch (semanticType) {
        case "search":
            return <Search size="0.85em" className="text-muted flex-shrink-0" />
        case "read":
            return <FileText size="0.85em" className="text-muted flex-shrink-0" />
        case "list":
            return <FolderOpen size="0.85em" className="text-muted flex-shrink-0" />
        case "edit":
            return <Pencil size="0.85em" className="text-muted flex-shrink-0" />
        case "write":
            return <FilePlus size="0.85em" className="text-muted flex-shrink-0" />
        case "git":
            return <GitBranch size="0.85em" className="text-muted flex-shrink-0" />
        case "verify":
            return <CheckCircle2 size="0.85em" className="text-muted flex-shrink-0" />
        default:
            return <Terminal size="0.85em" className="text-muted flex-shrink-0" />
    }
}

function durationLabel(durationMs: number | undefined): string | undefined {
    if (durationMs === undefined) return undefined
    if (durationMs < 1000) return `${Math.round(durationMs)}ms`
    return `${(durationMs / 1000).toFixed(durationMs < 10_000 ? 1 : 0)}s`
}

export const bashRenderer: GroupRenderer<BashGroup> = {
    getLabel: (group) => {
        if (group.description) {
            return group.description.length > 40 ? `${group.description.slice(0, 40)}...` : group.description
        }
        return classifyBashCommand(group.command).label
    },
    getIcon: (group) => getSemanticIcon(classifyBashCommand(group.command).semanticType),
    getStatusIcon: (group) => {
        if (group.isError) return <AlertCircle size="1em" className="text-error flex-shrink-0" />
        return null
    },
    getHeaderInfo: (group) => {
        const presentation = getCommandOutputPresentation(group.command, group.result)
        const duration = durationLabel(group.durationMs)
        if (presentation.kind === "git_status") {
            return <span className="text-muted text-xs">{[`${presentation.entries.length} files`, duration].filter(Boolean).join(" · ")}</span>
        }
        if (presentation.kind === "git_log") {
            return <span className="text-muted text-xs">{[`${presentation.commits.length} commits`, duration].filter(Boolean).join(" · ")}</span>
        }
        if (presentation.kind === "verification" && !group.isError) {
            return <span className="text-success text-xs">{[presentation.summary, duration].filter(Boolean).join(" · ")}</span>
        }
        if (group.sessionId !== undefined) return <span className="text-muted text-xs">session {group.sessionId}</span>
        if (duration) return <span className="text-muted text-xs">{duration}</span>
        return null
    },
    renderContent: (group, ctx) => <BashContent group={group} ctx={ctx} />,
}
